import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { NotificationApi } from '../API/NotificationApi';
import { useAuth } from '../Auth/AuthProvider';
import NotificationPopup from '../Components/NotificationPopup';
import { Notification } from '../Types/Notification';

const NOTIFICATIONS_RELOAD_INTERVAL = 10000;

interface NotificationContextType {
  notifications: Notification[];
  error: Error | null;
  reload: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  doNotDisturb: boolean;
  setDoNotDisturb: (doNotDisturb: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    );
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { userId } = useAuth();
  const [notifications, setNotifications] = useState<
    Notification[] | undefined
  >();
  const [error, setError] = useState<Error | null>(null);
  const [doNotDisturb, setDoNotDisturb] = useState(false);
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
  const [loadTriggeredAt, setLoadTriggeredAt] = useState<Date>();

  // States for popup notification system
  const [activePopupNotification, setActivePopupNotification] = useState<
    Notification | null | undefined
  >(undefined);
  const [pendingPopupNotifications, setPendingPopupNotifications] = useState<
    Notification[]
  >([]);
  const [ignoredNotifications, setIgnoredNotifications] = useState<
    Notification[]
  >([]);

  const notificationApi = new NotificationApi();

  // Simple fetch function - only responsible for getting notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setError(null);
      const data = await notificationApi.getNotifications();
      setNotifications(data);

      const newPendingNotifications = data
        .filter(
          (n) =>
            n.id !== activePopupNotification?.id &&
            !ignoredNotifications.some((i) => i.id === n.id) &&
            n.showModal,
        )
        .filter((n) => !n.readAt)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setPendingPopupNotifications(newPendingNotifications);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch notifications'),
      );
    }
  }, [activePopupNotification, ignoredNotifications, loadTriggeredAt]);

  // Initial fetch when component mounts or userId changes
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return;
    }

    fetchNotifications();
  }, [userId, fetchNotifications]);

  // Polling effect - checks every 10 seconds
  useEffect(() => {
    if (!userId) return;
    if (isMarkingAsRead) return;

    fetchNotifications().then(() => {
      // Schedule next check after 10 seconds
      const timeoutId = setTimeout(
        () => setLoadTriggeredAt(new Date()),
        NOTIFICATIONS_RELOAD_INTERVAL,
      );
      return () => clearTimeout(timeoutId);
    });
  }, [userId, loadTriggeredAt, isMarkingAsRead, fetchNotifications]);

  // Show notification once incoming or when exiting DnD mode
  useEffect(() => {
    if (
      !doNotDisturb &&
      pendingPopupNotifications.length > 0 &&
      !activePopupNotification
    ) {
      // Show the first stored notification when exiting DnD mode
      const nextNotification = pendingPopupNotifications.pop();
      setActivePopupNotification(nextNotification);
      setPendingPopupNotifications([...pendingPopupNotifications]);
    }
  }, [doNotDisturb, pendingPopupNotifications, activePopupNotification]);

  const handleClosePopup = useCallback(() => {
    setIgnoredNotifications([...ignoredNotifications, activePopupNotification]);
    setActivePopupNotification(null);
  }, [activePopupNotification, pendingPopupNotifications]);

  const markAsRead = async (id: string) => {
    setIsMarkingAsRead(true);

    try {
      // First update in memory immediately
      setNotifications((currentNotifications = []) =>
        currentNotifications.map((notification) =>
          notification.id === id
            ? { ...notification, readAt: new Date() }
            : notification,
        ),
      );

      // Also update in stored notifications if present
      setPendingPopupNotifications((stored) =>
        stored.filter((notification) => notification.id !== id),
      );

      // Then send request to server
      await notificationApi.markAsRead(id);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Optionally revert the in-memory change on error
      // but per requirements we'll keep the optimistic update
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const markAllAsRead = async () => {
    // Get all unread notifications
    const unreadNotifications = notifications?.filter((n) => !n.readAt) || [];
    if (unreadNotifications.length === 0) return;

    setIsMarkingAsRead(true);

    try {
      // First update all in memory immediately
      setNotifications((currentNotifications = []) =>
        currentNotifications.map((notification) =>
          !notification.readAt
            ? { ...notification, readAt: new Date() }
            : notification,
        ),
      );

      // Also clear stored notifications
      setPendingPopupNotifications([]);
      setActivePopupNotification(null);

      // Then send requests to server for each notification
      await Promise.all(
        unreadNotifications.map((notification) =>
          notificationApi.markAsRead(notification.id),
        ),
      );
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      // Keep the optimistic updates even if there's an error
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const value = {
    notifications: notifications ?? [],
    error,
    reload: fetchNotifications,
    markAsRead,
    markAllAsRead,
    doNotDisturb,
    setDoNotDisturb,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {activePopupNotification && (
        <NotificationPopup
          notification={activePopupNotification}
          onClose={handleClosePopup}
          onMarkAsRead={markAsRead}
        />
      )}
    </NotificationContext.Provider>
  );
};
